import React, { ReactNode } from 'react';
import MuiDrawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { SxProps, Theme } from '@mui/material';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  footerContent?: ReactNode;
  width?: string | number;
  sx?: SxProps<Theme>;
}

export const Drawer = ({
  isOpen,
  onClose,
  title = 'Drawer Title',
  children,
  footerContent,
  width = 400,
  sx,
}: DrawerProps) => {
  // Resolve width safely if caller passed 'w-96' or similar
  const resolvedWidth =
    typeof width === 'number'
      ? width
      : width === 'w-96'
      ? 384
      : width.startsWith('w-')
      ? 420
      : width;

  return (
    <MuiDrawer
      anchor="right"
      open={Boolean(isOpen)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: resolvedWidth },
          maxWidth: '100vw',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          borderLeft: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          ...sx,
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(3px)',
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
        },
      }}
    >
      {/* Drawer Header */}
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
          color: '#ffffff',
          overflow: 'hidden',
          px: { xs: 2, sm: 2.5 },
          py: 2,
        }}
      >
        {/* Decorative Circles */}
        <Box
          sx={{
            position: 'absolute',
            top: -64,
            right: -64,
            width: 160,
            height: 160,
            borderRadius: '50%',
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              color: '#ffffff',
              letterSpacing: '-0.01em',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>

          <IconButton
            aria-label="Close drawer"
            onClick={onClose}
            size="small"
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '10px',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                transform: 'scale(1.05)',
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Drawer Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 2, sm: 2.5 },
          bgcolor: 'slate.50',
        }}
      >
        {children}
      </Box>

      {/* Optional Drawer Footer */}
      {footerContent && (
        <Box
          sx={{
            flexShrink: 0,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.03)',
          }}
        >
          {footerContent}
        </Box>
      )}
    </MuiDrawer>
  );
};

export default Drawer;
